import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  GuideWrapper,
  Hero,
  BackButton,
  Title,
  Subtitle,
  Container,
  Introduction,
  SectionTitle,
  HeroWarningBox,
  CTASection,
  PrimaryButton,
  SecondaryButton,
  InfoSection,
  InfoBlock
} from './TyreGuidePage.styles';

// Extra styled-components localized to TyreCareGuide
const DiagnosticsContainer = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--cardBorder);
  border-radius: 24px;
  padding: 40px;
  margin: 40px 0 60px;
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const DiagnoserGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 40px;
  margin-top: 30px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WearOptionButton = styled.button`
  background: ${props => props.active ? 'rgba(232, 197, 71, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.active ? 'var(--accentGold)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 16px 20px;
  color: ${props => props.active ? '#ffffff' : '#cfc9be'};
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: ${props => props.active ? 'rgba(232, 197, 71, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.active ? 'var(--accentGold)' : 'rgba(255, 255, 255, 0.2)'};
    color: #ffffff;
    transform: translateY(-2px);
  }

  &::after {
    content: '→';
    opacity: ${props => props.active ? '1' : '0.3'};
    transform: translateX(${props => props.active ? '0' : '-5px'});
    transition: all 0.3s ease;
  }
`;

const DetailsDisplay = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;

  h3 {
    font-size: 1.35rem;
    color: var(--accentGold);
    margin: 0;
    font-weight: 700;
  }
`;

const GraphicBox = styled.div`
  width: 100%;
  height: 140px;
  background: #0f1115;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1.5px dashed rgba(255, 255, 255, 0.1);
  padding: 10px;
`;

const CauseRemedyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled.div`
  h4 {
    font-size: 0.95rem;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 8px 0;
    font-weight: 700;
  }

  p {
    font-size: 0.95rem;
    color: #cfc9be;
    line-height: 1.6;
    margin: 0;
  }
`;

const RotationContainer = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--cardBorder);
  border-radius: 24px;
  padding: 40px;
  margin: 40px 0 60px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const TabRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #e8c547 0%, #d4af37 100%)' : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.active ? 'var(--accentGold)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 25px;
  padding: 10px 24px;
  color: ${props => props.active ? '#0b0f19' : '#cfc9be'};
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.active ? '0 5px 15px rgba(212, 175, 55, 0.3)' : 'none'};
    border-color: ${props => props.active ? 'var(--accentGold)' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const RotationDisplay = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const RotationGraphic = styled.div`
  background: #0f1115;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: center;
  align-items: center;
  aspect-ratio: 4/3;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const RotationText = styled.div`
  text-align: left;

  h3 {
    font-size: 1.4rem;
    color: #ffffff;
    margin: 0 0 15px 0;
    font-weight: 700;
  }

  p {
    font-size: 1rem;
    color: #cfc9be;
    line-height: 1.6;
    margin: 0 0 20px 0;
  }

  ol {
    padding-left: 20px;
    margin: 0;

    li {
      margin-bottom: 10px;
      color: #cfc9be;
      line-height: 1.5;
      font-size: 0.95rem;
      
      strong {
        color: #ffffff;
      }
    }
  }
`;

const NitrogenSection = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%);
  border: 1px solid rgba(212, 175, 55, 0.12);
  border-radius: 24px;
  padding: 40px;
  margin: 40px 0 60px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    padding: 24px;
    margin: 30px 0 50px;
  }
`;

const NitrogenList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  li {
    font-size: 1.05rem;
    line-height: 1.6;
    color: #cfc9be;
    display: flex;
    align-items: flex-start;

    &::before {
      content: "✓";
      color: var(--accentGold);
      font-weight: bold;
      margin-right: 12px;
      font-size: 1.2rem;
      line-height: 1.2;
      flex-shrink: 0;
    }

    strong {
      color: var(--accentGold);
      font-weight: 700;
    }
  }
`;

const WEAR_DIAGNOSTICS = {
  under: {
    title: 'Under-Inflation (Edge Wear)',
    desc: 'Wear concentrated on both the outer edges (shoulders) of the tyre tread, while the center remains less worn.',
    cause: 'Running the tyre with less than the recommended pressure causes the tyre to sag in the middle, shifting the vehicle weight onto the outer shoulders.',
    remedy: 'Inflate tyres to the vehicle manufacturer\'s recommended PSI (found on the driver door sticker or owner\'s manual). Check tyre pressure weekly.',
    svg: (
      <svg viewBox="0 0 200 120" width="100%" height="100%">
        <line x1="10" y1="100" x2="190" y2="100" stroke="#4b5563" strokeWidth="3" />
        <rect x="70" y="20" width="60" height="20" rx="3" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
        <path d="M 70,30 C 50,30 40,50 45,85 C 45,95 65,95 90,92 C 100,92 100,92 110,92 C 135,95 155,95 155,85 C 160,50 150,30 130,30 Z" fill="#1f2937" stroke="#111827" strokeWidth="2" />
        <path d="M 45,85 C 48,93 60,94 70,93.5" fill="none" stroke="#ef4444" strokeWidth="4" strokeLinecap="round" opacity="0.8" />
        <path d="M 155,85 C 152,93 140,94 130,93.5" fill="none" stroke="#ef4444" strokeWidth="4" strokeLinecap="round" opacity="0.8" />
        <text x="100" y="115" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="700">Excessive Shoulder Wear</text>
      </svg>
    )
  },
  over: {
    title: 'Over-Inflation (Center Wear)',
    desc: 'Tread wear concentrated in the center of the tyre, leaving the outer edges with significantly more tread remaining.',
    cause: 'The tyre is inflated beyond the recommended pressure. The excess pressure causes the center of the tyre tread to bulge outward and bear all the contact force.',
    remedy: 'Release excess pressure to match the manufacturer\'s recommended PSI. Never inflate tyres to the "Max PSI" written on the tyre sidewall.',
    svg: (
      <svg viewBox="0 0 200 120" width="100%" height="100%">
        <line x1="10" y1="100" x2="190" y2="100" stroke="#4b5563" strokeWidth="3" />
        <rect x="70" y="20" width="60" height="20" rx="3" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
        <path d="M 70,30 C 50,30 40,50 42,80 C 42,90 70,100 100,100 C 130,100 158,90 158,80 C 160,50 150,30 130,30 Z" fill="#1f2937" stroke="#111827" strokeWidth="2" />
        <path d="M 85,99 C 100,100.5 115,99" fill="none" stroke="#ef4444" strokeWidth="4" strokeLinecap="round" opacity="0.8" />
        <text x="100" y="115" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="700">Excessive Center Wear</text>
      </svg>
    )
  },
  alignment: {
    title: 'Misalignment (Camber Wear)',
    desc: 'Tread wear that is significantly higher on one side (either the inner or outer edge of the tread) than the other.',
    cause: 'The wheel alignment angles are out of spec, particularly the camber angle, which causes the tyre to run tilted rather than vertical on the road.',
    remedy: 'Perform a professional 3D Wheel Alignment immediately to bring wheels back to specification. This prevents the uneven wear from worsening.',
    svg: (
      <svg viewBox="0 0 200 120" width="100%" height="100%">
        <line x1="10" y1="100" x2="190" y2="100" stroke="#4b5563" strokeWidth="3" />
        <g transform="rotate(5 100 60)">
          <rect x="70" y="20" width="60" height="20" rx="3" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
          <path d="M 70,30 C 50,30 40,50 45,85 C 45,95 155,95 155,85 C 160,50 150,30 130,30 Z" fill="#1f2937" stroke="#111827" strokeWidth="2" />
          <path d="M 45,85 C 48,93 65,95 75,95" fill="none" stroke="#ef4444" strokeWidth="4" strokeLinecap="round" opacity="0.8" />
        </g>
        <text x="100" y="115" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="700">Inner / Outer Edge Wear</text>
      </svg>
    )
  },
  cupping: {
    title: 'Cupping / Scalloping',
    desc: 'Wavy, smooth dips and divots worn into the tyre tread at regular intervals around the circumference.',
    cause: 'Worn or damaged suspension components (shocks, struts, or bushings) that fail to keep the tyre firmly pressed against the road, causing the wheel to bounce as it rolls.',
    remedy: 'Inspect the vehicle suspension, replace worn shock absorbers or struts, and have the wheels professionally balanced and aligned.',
    svg: (
      <svg viewBox="0 0 200 120" width="100%" height="100%">
        <line x1="10" y1="100" x2="190" y2="100" stroke="#4b5563" strokeWidth="3" />
        <rect x="70" y="20" width="60" height="20" rx="3" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
        <path d="M 70,30 C 50,30 40,50 45,85 C 45,95 60,90 70,95 C 80,95 90,90 100,95 C 110,95 120,90 130,95 C 140,95 155,92 155,85 C 160,50 150,30 130,30 Z" fill="#1f2937" stroke="#111827" strokeWidth="2" />
        <path d="M 68,95 Q 73,98 78,94" fill="none" stroke="#ef4444" strokeWidth="3" opacity="0.8" />
        <path d="M 98,95 Q 103,98 108,94" fill="none" stroke="#ef4444" strokeWidth="3" opacity="0.8" />
        <path d="M 128,95 Q 133,98 138,94" fill="none" stroke="#ef4444" strokeWidth="3" opacity="0.8" />
        <text x="100" y="115" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="700">Wavy Cupped Tread Dips</text>
      </svg>
    )
  },
  feathering: {
    title: 'Feathering (Toe Wear)',
    desc: 'Tread blocks worn smooth on one side of each block, but feeling sharp and jagged when rubbed in the opposite direction.',
    cause: 'Alignment issue where tyres are pointed too far inward (toe-in) or outward (toe-out). This forces the tread blocks to scuff sideways against the road.',
    remedy: 'Have the steering linkage and suspension inspected, followed by a professional wheel alignment to adjust the toe angles to factory specifications.',
    svg: (
      <svg viewBox="0 0 200 120" width="100%" height="100%">
        <line x1="10" y1="100" x2="190" y2="100" stroke="#4b5563" strokeWidth="3" />
        <rect x="70" y="20" width="60" height="20" rx="3" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
        <path d="M 70,30 C 50,30 40,50 45,85 C 45,95 155,95 155,85 C 160,50 150,30 130,30 Z" fill="#1f2937" stroke="#111827" strokeWidth="2" />
        <path d="M 50,95 L 60,91 M 60,95 L 70,91 M 70,95 L 80,91 M 80,95 L 90,91 M 90,95 L 100,91 M 100,95 L 110,91 M 110,95 L 120,91 M 120,95 L 130,91 M 130,95 L 140,91 M 140,95 L 150,91" stroke="#ef4444" strokeWidth="2.5" />
        <text x="100" y="115" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="700">Sharp Sawtooth Wear Edges</text>
      </svg>
    )
  }
};

const ROTATION_PATTERNS = {
  fwd: {
    title: 'Front-Wheel Drive (FWD) Pattern',
    desc: 'Front-wheel drive vehicles wear out front tyres faster because they handle steering, braking, and engine power. Rear tyres move forward and cross sides, while front tyres move straight back.',
    instructions: [
      { step: 1, text: 'Move <strong>Rear Left (RL)</strong> to <strong>Front Right (FR)</strong>.' },
      { step: 2, text: 'Move <strong>Rear Right (RR)</strong> to <strong>Front Left (FL)</strong>.' },
      { step: 3, text: 'Move <strong>Front Left (FL)</strong> straight back to <strong>Rear Left (RL)</strong>.' },
      { step: 4, text: 'Move <strong>Front Right (FR)</strong> straight back to <strong>Rear Right (RR)</strong>.' }
    ],
    svg: (
      <svg viewBox="0 0 240 300" width="100%" height="100%">
        <rect x="50" y="30" width="140" height="240" rx="10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
        <line x1="60" y1="70" x2="180" y2="70" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <line x1="60" y1="230" x2="180" y2="230" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <text x="120" y="22" textAnchor="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">FRONT (ENGINE)</text>
        <rect x="30" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="47" y="75" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">FL</text>
        <rect x="176" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="193" y="75" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">FR</text>
        <rect x="30" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#4b5563" strokeWidth="1.5" />
        <text x="47" y="235" textAnchor="middle" dominantBaseline="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">RL</text>
        <rect x="176" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#4b5563" strokeWidth="1.5" />
        <text x="193" y="235" textAnchor="middle" dominantBaseline="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">RR</text>
        <path d="M 64,195 Q 120,130 176,105" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 176,195 Q 120,130 64,105" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 47,105 L 47,192" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 193,105 L 193,192" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <defs>
          <marker id="arrow-rot" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#e8c547" />
          </marker>
        </defs>
      </svg>
    )
  },
  rwd: {
    title: 'Rear-Wheel Drive (RWD) Pattern',
    desc: 'Rear-wheel drive vehicles wear out rear tyres due to engine torque delivery. Front tyres move back and cross sides, while rear tyres move straight forward to the front.',
    instructions: [
      { step: 1, text: 'Move <strong>Front Left (FL)</strong> to <strong>Rear Right (RR)</strong>.' },
      { step: 2, text: 'Move <strong>Front Right (FR)</strong> to <strong>Rear Left (RL)</strong>.' },
      { step: 3, text: 'Move <strong>Rear Left (RL)</strong> straight forward to <strong>Front Left (FL)</strong>.' },
      { step: 4, text: 'Move <strong>Rear Right (RR)</strong> straight forward to <strong>Front Right (FR)</strong>.' }
    ],
    svg: (
      <svg viewBox="0 0 240 300" width="100%" height="100%">
        <rect x="50" y="30" width="140" height="240" rx="10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
        <line x1="60" y1="70" x2="180" y2="70" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <line x1="60" y1="230" x2="180" y2="230" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <text x="120" y="22" textAnchor="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">FRONT</text>
        <rect x="30" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#4b5563" strokeWidth="1.5" />
        <text x="47" y="75" textAnchor="middle" dominantBaseline="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">FL</text>
        <rect x="176" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#4b5563" strokeWidth="1.5" />
        <text x="193" y="75" textAnchor="middle" dominantBaseline="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">FR</text>
        <rect x="30" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="47" y="235" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">RL</text>
        <rect x="176" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="193" y="235" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">RR</text>
        <path d="M 64,105 Q 120,170 176,195" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 176,105 Q 120,170 64,195" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 47,195 L 47,108" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 193,195 L 193,108" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <defs>
          <marker id="arrow-rot" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#e8c547" />
          </marker>
        </defs>
      </svg>
    )
  },
  awd: {
    title: 'AWD / 4WD (X-Pattern)',
    desc: 'All-Wheel Drive and Four-Wheel Drive vehicles distribute engine power dynamically. To achieve balanced and even wear, tyres are rotated diagonally in a full cross pattern.',
    instructions: [
      { step: 1, text: 'Move <strong>Front Left (FL)</strong> diagonally to <strong>Rear Right (RR)</strong>.' },
      { step: 2, text: 'Move <strong>Front Right (FR)</strong> diagonally to <strong>Rear Left (RL)</strong>.' },
      { step: 3, text: 'Move <strong>Rear Left (RL)</strong> diagonally to <strong>Front Right (FR)</strong>.' },
      { step: 4, text: 'Move <strong>Rear Right (RR)</strong> diagonally to <strong>Front Left (FL)</strong>.' }
    ],
    svg: (
      <svg viewBox="0 0 240 300" width="100%" height="100%">
        <rect x="50" y="30" width="140" height="240" rx="10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
        <line x1="60" y1="70" x2="180" y2="70" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <line x1="60" y1="230" x2="180" y2="230" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <text x="120" y="22" textAnchor="middle" fill="#a1a1aa" fontSize="11" fontWeight="700">FRONT (AWD)</text>
        <rect x="30" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="47" y="75" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">FL</text>
        <rect x="176" y="40" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="193" y="75" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">FR</text>
        <rect x="30" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="47" y="235" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">RL</text>
        <rect x="176" y="200" width="34" height="60" rx="5" fill="#1f2937" stroke="#e8c547" strokeWidth="2" />
        <text x="193" y="235" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="11" fontWeight="700">RR</text>
        <path d="M 64,105 Q 120,170 176,195" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 176,105 Q 120,170 64,195" fill="none" stroke="#e8c547" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 64,195 Q 120,130 176,105" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <path d="M 176,195 Q 120,130 64,105" fill="none" stroke="#3b82f6" strokeWidth="3" markerEnd="url(#arrow-rot)" />
        <defs>
          <marker id="arrow-rot" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#e8c547" />
          </marker>
        </defs>
      </svg>
    )
  }
};

const TyreCareGuidePage = () => {
  const navigate = useNavigate();
  const [activeWear, setActiveWear] = useState('under');
  const [activeRotation, setActiveRotation] = useState('fwd');

  const currentWear = WEAR_DIAGNOSTICS[activeWear];
  const currentRotation = ROTATION_PATTERNS[activeRotation];

  return (
    <GuideWrapper>
      <Hero>
        <div className="back-btn-container">
          <BackButton onClick={() => navigate('/tyres')}>
            ← Back to Tyres
          </BackButton>
        </div>
        <Title>Tyre Care & Maintenance Guide</Title>
        <Subtitle>
          Extend your tyre life, optimize fuel efficiency, and ensure maximum road safety with proper tyre upkeep.
        </Subtitle>
        <HeroWarningBox>
          <div className="warning-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" fill="rgba(239, 68, 68, 0.2)" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="warning-text">
            <strong>Safety Note:</strong> Tyres with tread depth under 1.6 mm are highly unsafe to drive. Inspect tread depth monthly using the Tread Wear Indicator (TWI).
          </div>
        </HeroWarningBox>
      </Hero>

      <Container>
        <Introduction>
          <p>
            Tyres carry the entire load of your vehicle and are crucial to braking, traction, and control. Simple routines like keeping them properly inflated, balancing, aligning, and rotating regularly can double their lifetime and prevent catastrophic tyre failures.
          </p>
        </Introduction>

        {/* Wear Diagnostics Section */}
        <DiagnosticsContainer>
          <h2 style={{ textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center', color: '#ffffff', margin: '0 0 10px 0', fontSize: '1.4rem' }}>
            Tyre Wear Diagnostics Tool
          </h2>
          <p style={{ color: '#a1a1aa', textAlign: 'center', marginBottom: '30px', fontSize: '0.95rem' }}>
            Identify abnormalities on your tyre tread to diagnose alignment, suspension, or inflation issues early:
          </p>

          <DiagnoserGrid>
            <ButtonColumn>
              {Object.keys(WEAR_DIAGNOSTICS).map((key) => (
                <WearOptionButton
                  key={key}
                  active={activeWear === key}
                  onClick={() => setActiveWear(key)}
                >
                  {WEAR_DIAGNOSTICS[key].title}
                </WearOptionButton>
              ))}
            </ButtonColumn>

            {currentWear && (
              <DetailsDisplay>
                <DetailsHeader>
                  <h3>{currentWear.title}</h3>
                </DetailsHeader>
                <GraphicBox>
                  {currentWear.svg}
                </GraphicBox>
                <p style={{ fontSize: '1rem', color: '#ffffff', margin: '0', lineHeight: '1.5' }}>
                  <strong>Description:</strong> {currentWear.desc}
                </p>
                <CauseRemedyGrid>
                  <InfoBox>
                    <h4>Possible Cause</h4>
                    <p>{currentWear.cause}</p>
                  </InfoBox>
                  <InfoBox>
                    <h4>Corrective Action</h4>
                    <p>{currentWear.remedy}</p>
                  </InfoBox>
                </CauseRemedyGrid>
              </DetailsDisplay>
            )}
          </DiagnoserGrid>
        </DiagnosticsContainer>

        {/* Drivetrain Rotation Section */}
        <RotationContainer>
          <h2 style={{ textTransform: 'uppercase', letterSpacing: '1px', color: '#ffffff', margin: '0 0 10px 0', fontSize: '1.4rem' }}>
            Recommended Tyre Rotation Patterns
          </h2>
          <p style={{ color: '#a1a1aa', marginBottom: '30px', fontSize: '0.95rem' }}>
            Regular rotation (every 8,000 to 10,000 km) ensures even wear across all four tyres. Select your vehicle's drivetrain to see the pattern:
          </p>

          <TabRow>
            {Object.keys(ROTATION_PATTERNS).map((key) => (
              <TabButton
                key={key}
                active={activeRotation === key}
                onClick={() => setActiveRotation(key)}
              >
                {ROTATION_PATTERNS[key].title.split(' Pattern')[0]}
              </TabButton>
            ))}
          </TabRow>

          {currentRotation && (
            <RotationDisplay>
              <RotationGraphic>
                {currentRotation.svg}
              </RotationGraphic>
              <RotationText>
                <h3>{currentRotation.title}</h3>
                <p>{currentRotation.desc}</p>
                <h4 style={{ color: '#ffffff', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '0.5px', margin: '0 0 12px 0' }}>
                  Step-by-Step Rotation:
                </h4>
                <ol>
                  {currentRotation.instructions.map((inst) => (
                    <li key={inst.step} dangerouslySetInnerHTML={{ __html: inst.text }}></li>
                  ))}
                </ol>
              </RotationText>
            </RotationDisplay>
          )}
        </RotationContainer>

        {/* Pillars of Tyre Maintenance */}
        <SectionTitle>Four Pillars of Tyre Maintenance</SectionTitle>
        <InfoSection>
          <InfoBlock>
            <h3>1. Proper Pressure (Inflation)</h3>
            <p>
              Incorrect pressure is the leading cause of tyre damage. Under-inflation generates excess heat, decreases fuel economy, and accelerates edge wear. Over-inflation makes tyres rigid, decreasing comfort and traction while creating center wear.
            </p>
            <ul>
              <li>Check pressures weekly and always when tyres are cold (driven less than 2 km).</li>
              <li>Always check the spare tyre as well.</li>
              <li>Use the vehicle door placard PSI recommendation, NOT the tyre sidewall maximum.</li>
            </ul>
          </InfoBlock>

          <InfoBlock>
            <h3>2. Wheel Alignment</h3>
            <p>
              Alignment refers to adjusting the vehicle steering and suspension angles so that the tyres contact the road flat. Potholes and kerb strikes throw alignment out, causing vehicle pulling and swift edge wear.
            </p>
            <ul>
              <li>Align wheels every 10,000 km or immediately if the steering pulls to one side.</li>
              <li>Check alignment if you notice off-center steering wheels or steering vibrations.</li>
              <li>Ensures optimal directional control and safety.</li>
            </ul>
          </InfoBlock>

          <InfoBlock>
            <h3>3. Wheel Balancing</h3>
            <p>
              Balancing corrects weight imbalances in the tyre/wheel assembly. Even minor weight inconsistencies cause high-speed vibrations in the steering wheel or body, causing premature tyre wear and early failure of suspension shock absorbers.
            </p>
            <ul>
              <li>Balance tyres whenever new tyres are mounted.</li>
              <li>Rebalance wheels if you experience vibrations at highway speeds (80–120 km/h).</li>
              <li>Extend wheel bearing and suspension component life.</li>
            </ul>
          </InfoBlock>

          <InfoBlock>
            <h3>4. Tread Depth & Inspection</h3>
            <p>
              Inspect tread depth and look for damage (cracks, bulges, cuts, or embedded nails) weekly. The penny test or TWI blocks help check tread height. Bulges or bubbles on the sidewall indicate structural cord damage and require instant replacement.
            </p>
            <ul>
              <li>Inspect TWI indicators in the main tyre grooves regularly.</li>
              <li>Immediately replace tyres showing sidewall bubbles or deep cuts exposing internal steel cords.</li>
              <li>Replace tyres older than 5 years as tyre compound degrades over time.</li>
            </ul>
          </InfoBlock>
        </InfoSection>

        {/* Why Nitrogen is Used in Tyres Section */}
        <SectionTitle>Why Nitrogen Is Used in Tyres</SectionTitle>
        <NitrogenSection>
          <NitrogenList>
            <li>
              <span><strong>Slower pressure loss:</strong> Nitrogen escapes from tyres much slower than oxygen, so you don’t need top-ups as often.</span>
            </li>
            <li>
              <span><strong>Improved fuel efficiency:</strong> Correct pressure means better rolling resistance, which can help mileage.</span>
            </li>
            <li>
              <span><strong>Better stability:</strong> Especially over long drives, when tyres heat up, nitrogen stays more stable.</span>
            </li>
            <li>
              <span><strong>Less moisture:</strong> Regular air carries humidity, which may lead to corrosion inside rims over time. Nitrogen is dry.</span>
            </li>
            <li>
              <span><strong>Longer tyre life:</strong> Reduced pressure swings and moisture can lead to even tread wear.</span>
            </li>
          </NitrogenList>
          <p style={{ fontSize: '0.88rem', color: '#a1a1aa', fontStyle: 'italic', marginTop: '24px', textAlign: 'left', lineHeight: '1.4' }}>
            *These are the most cited benefits of nitrogen in tyres. They’re real, but they also depend a lot on how often and how far you drive. You don’t need to be a car expert to notice the benefits, they show up in small, helpful ways over time.
          </p>
        </NitrogenSection>

        {/* Cross Guide Links */}
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', margin: '40px 0 0 0', flexWrap: 'wrap' }}>
          <SecondaryButton onClick={() => navigate('/tyres/how-to-read-a-tyre')} style={{ borderColor: 'var(--accentGold)', color: 'var(--accentGold)' }}>
            ← Read Tyre Markings Guide
          </SecondaryButton>
        </div>

        {/* Contact / Jindal Tyres Promo CTA */}
        <CTASection>
          <h3>Need Professional Wheel Alignment or Tyre Service?</h3>
          <p>
            Jindal Tyres features Nagpur's most advanced 3D wheel alignment, computerised wheel balancing, and professional tyre rotation. We supply premium tyre brands with free fitting and alignment packages.
          </p>
          <div className="cta-buttons">
            <PrimaryButton href="tel:7020847854">
              Call Tyre Experts
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/tyres')}>
              Explore Services
            </SecondaryButton>
          </div>
        </CTASection>
      </Container>
    </GuideWrapper>
  );
};

export default TyreCareGuidePage;
